import { useContext, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import styles from "./mortgage-view.module.scss";
import InputWithHeader from "../../components/Input/InputWithHeader";
import FileUploadInput from "../../components/Input/file-upload-input";
import { useQuery } from "../../hooks/useQuery";
import { useHistory } from "react-router";
import ToastContext from "../../contexts/ToastContext";
import LoadingContext from "../../contexts/LoadingContext";
import {
  generateAccountStatement,
  mortgageDocumentUploadApi,
  mortgageGetAccountNameApi,
  mortgageValidateAccVerifyOtpApi,
  submitMortgageApplication,
} from "../../swr/mortgage";
import { getBankCodes, NameEnquiry } from "../../swr/wallet";
import { EachBankObj } from "../../types/Wallet";
import {
  DocumentTypes,
  ValidateAccountRequest,
  ValidateAccountResponseData,
  ValidateAccountVerifyOtpResData,
} from "../../types/mortgage";
import ModalLayout from "../../components/ModalLayout";
import SuccessModal from "../../components/Modal/success-modal";
import GenerateStatementModal from "../../components/Modal/generate-statement-modal";
import ValidateAccountOtpModal from "../../components/Modal/validate-account-otp-modal";
import PopUp from "../../components/Insurance/PopUp";

type DocUploadReq = {
  hasAccessAccount?: boolean;
  accountNumber?: string;
  accountName?: string;
  bankName?: string;
  propertyPhoto?: File;
  statementOfAccount?: File;
  titledDocument?: File;
  aceptanceLetter?: File;
  meansOfIdentification?: File;
  letterOfEmployment?: File;
  paySlip?: File;
  evidenceOfPayment?: File;
  businessRegistration?: File;
  proofOfResidence?: File;
  taxEvidence?: File;
  w2?: File;
  nationalInsurance?: File;
};

const fileSizeTest = (file: File | undefined) =>
  file ? file.size / (1024 * 1024) <= 10 : false;

const requiredFile = (msg: string) =>
  yup
    .mixed<File>()
    .required(msg)
    .test("fileSize", "File must be 10MB or less", fileSizeTest);

const optionalFile = () =>
  yup.mixed<File>().notRequired().default(undefined) as yup.MixedSchema<
    File | undefined
  >;

const buildSchema = (
  isBusinessApplicant: boolean,
  isDiaspora: boolean,
  statementGenerated: boolean
) =>
  yup.object({
    hasAccessAccount: yup.boolean().required("Please select an option"),

    accountNumber: yup
      .string()
      .defined()
      .when("hasAccessAccount", {
        is: (val: boolean | undefined) => typeof val === "boolean",
        then: (s) => s.min(1).required("Account number is required"),
        otherwise: (s) => s.default(""),
      }),

    accountName: yup
      .string()
      .defined()
      .when("hasAccessAccount", {
        is: false,
        then: (s) => s.min(1).required("Account name is required"),
        otherwise: (s) => s.default(""),
      }),

    bankName: yup
      .string()
      .defined()
      .when("hasAccessAccount", {
        is: false,
        then: (s) => s.min(1).required("Bank name is required"),
        otherwise: (s) => s.default(""),
      }),

    // Common to all
    propertyPhoto: requiredFile("Property photo is required"),
    // Not required once a statement has been generated digitally
    // (generation happens server-side and returns no downloadable file).
    statementOfAccount: statementGenerated
      ? optionalFile()
      : requiredFile("Statement Of Bank Account is required"),
    titledDocument: requiredFile("Titled Document of Property is required"),
    aceptanceLetter: requiredFile(
      "Acceptance letter of Property Owner is required"
    ),
    meansOfIdentification: requiredFile("Means of Identification is required"),

    // Salary only
    letterOfEmployment: !isBusinessApplicant
      ? requiredFile("Letter of Employment is required")
      : optionalFile(),
    paySlip: !isBusinessApplicant
      ? requiredFile("Last 6 months Payslip is required")
      : optionalFile(),
    // Nigeria salary only — diaspora applicants aren't asked for this.
    evidenceOfPayment:
      !isBusinessApplicant && !isDiaspora
        ? requiredFile("Evidence of Payment is required")
        : optionalFile(),

    // Business only
    businessRegistration: isBusinessApplicant
      ? requiredFile("Business Registration Document is required")
      : optionalFile(),

    // Diaspora required
    proofOfResidence: isDiaspora
      ? requiredFile("Proof of Residence is required")
      : optionalFile(),

    taxEvidence: isDiaspora
      ? requiredFile("Tax Evidence is required")
      : optionalFile(),

    // Diaspora optional
    w2: optionalFile(),
    nationalInsurance: optionalFile(),
  });

const DocumentUploadExtMort = () => {
  let query = useQuery();
  const isBusinessApplicant = query.get("epytppa") === "business";
  const isDiaspora = query.get("loc") === "1";
  const appId = query.get("dippa");

  const [statementGenerated, setStatementGenerated] = useState(false);

  const schema = useMemo(
    () => buildSchema(isBusinessApplicant, isDiaspora, statementGenerated),
    [isBusinessApplicant, isDiaspora, statementGenerated]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<DocUploadReq>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      hasAccessAccount: undefined,
      accountNumber: undefined,
      accountName: undefined,
      bankName: undefined,
      propertyPhoto: undefined,
      statementOfAccount: undefined,
      titledDocument: undefined,
      aceptanceLetter: undefined,
      meansOfIdentification: undefined,
      letterOfEmployment: undefined,
      paySlip: undefined,
      evidenceOfPayment: undefined,
      businessRegistration: undefined,
      proofOfResidence: undefined,
      taxEvidence: undefined,
      w2: undefined,
      nationalInsurance: undefined,
    },
  });
  const history = useHistory();

  const { openError, openSuccess } = useContext(ToastContext);
  const { setLoading } = useContext(LoadingContext);

  const [submitSuccessful, setSubmitSuccessful] = useState(false);

  const [accountResponse, setAccountResponse] =
    useState<ValidateAccountResponseData>();

  const [showAccountOtpModal, setShowAccountOtpModal] = useState(false);
  const [isVerifyingAccountOtp, setIsVerifyingAccountOtp] = useState(false);
  const [accountVerified, setAccountVerified] = useState(false);
  const [verifiedAccountName, setVerifiedAccountName] = useState<string>();

  const [banks, setBanks] = useState<EachBankObj[]>([]);

  const [showGenerateStatementModal, setShowGenerateStatementModal] =
    useState(false);
  const [isGeneratingStatement, setIsGeneratingStatement] = useState(false);

  const [dropDownStates, setDropDownStates] = useState({
    hasAccessAccount: false,
    bankName: false,
  });

  const toggleDropdown = (key: keyof typeof dropDownStates, open: boolean) => {
    setDropDownStates((prev) => ({ ...prev, [key]: open }));
  };

  const hasAccessAccount = watch("hasAccessAccount");
  const accountNumber = watch("accountNumber");
  const bankName = watch("bankName");

  const clickOutSuccessModal = () => {
    setSubmitSuccessful(false);
    history.push("/mortgage-categories");
  };

  const onSubmit = async (req: DocUploadReq) => {
    setLoading(true);
    try {
      const {
        hasAccessAccount,
        accountNumber,
        accountName,
        bankName,
        ...rest
      } = req;
 
      const entries = (Object.entries(rest) as [string, File][]).filter(
        ([, file]) => file != null
      );
 
      await mortgageDocumentUploadApi({
        MortgageApplicationId: appId ? Number(appId) : 0,
        DocumentTypes: entries.map(
          ([doc]) =>
            DocumentTypes.find((type) => type.finder === doc)?.value ?? 0
        ),
        Files: entries.map(([, file]) => file),
      });
 
      const { data, error } = await submitMortgageApplication(
        appId ? Number(appId) : 0
      );

      if (data) {
        setSubmitSuccessful(true);
      }

      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred"
        );
      }
    } catch (error: any) {
      openError(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "An error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const getAccountName = async (req: ValidateAccountRequest) => {
    try {
      const { data } = await mortgageGetAccountNameApi(req);
      if (data) {
        setAccountResponse(data?.data);
        setAccountVerified(false);
        setVerifiedAccountName(undefined);
        setShowAccountOtpModal(true);
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (
      hasAccessAccount === true &&
      accountNumber &&
      accountNumber?.length === 10 &&
      appId
    ) {
      getAccountName({
        accountNumber,
        mortgageApplicationId: Number(appId),
      });
    }
  }, [accountNumber, hasAccessAccount, appId]);

  const handleVerifyAccountOtp = async (otp: string) => {
    if (!appId) return;
    setIsVerifyingAccountOtp(true);
    try {
      const { data, error } = await mortgageValidateAccVerifyOtpApi({
        mortgageApplicationId: Number(appId),
        otp,
      });
      const result = data?.data as unknown as
        | ValidateAccountVerifyOtpResData
        | undefined;
      if (result) {
        setVerifiedAccountName(result.accountName);
        setAccountVerified(true);
        setShowAccountOtpModal(false);
      }
      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred"
        );
      }
    } catch (error) {
      openError("An error occurred");
    } finally {
      setIsVerifyingAccountOtp(false);
    }
  };

  const handleResendAccountOtp = () => {
    if (accountNumber && appId) {
      getAccountName({ accountNumber, mortgageApplicationId: Number(appId) });
    }
  };

  const enquireExternalAccountName = async (
    creditAccount: string,
    creditBankCode: string
  ) => {
    try {
      const response = await NameEnquiry({
        requestBody: {
          credit_bank_code: creditBankCode,
          credit_account: creditAccount,
        },
      });
      if (response?.succeeded && response.data) {
        setValue("accountName", response.data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (
      hasAccessAccount === false &&
      accountNumber &&
      accountNumber?.length === 10 &&
      bankName
    ) {
      const bankCode = banks.find(
        (bank) => bank.bankName === bankName
      )?.bankCode;
      if (bankCode) {
        enquireExternalAccountName(accountNumber, bankCode);
      }
    }
  }, [accountNumber, bankName, hasAccessAccount, banks]);

  const fetchBankCodes = async () => {
    try {
      const codeObj = await getBankCodes();
      if (codeObj?.succeeded && codeObj.data) {
        setBanks(codeObj.data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (hasAccessAccount === false && banks.length === 0) {
      fetchBankCodes();
    }
  }, [hasAccessAccount]);

  const handleGenerateStatement = async (fromDate: string, toDate: string) => {
    setIsGeneratingStatement(true);
    try {
      const appId = query.get("dippa");
      const { data, error } = await generateAccountStatement(
        { fromDate, toDate },
        appId ? Number(appId) : 0
      );
      if (data) {
        setShowGenerateStatementModal(false);
        if (data.code === 1) {
          setStatementGenerated(true);
          openSuccess("Statement Generated Successfully");
        } else {
          openError(
            data.message ||
              "We couldn't generate your statement automatically. Please upload it manually."
          );
        }
      }
      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred"
        );
      }
    } catch (error) {
      openError("An error occurred");
    } finally {
      setIsGeneratingStatement(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.container}>
          <span className={styles.instruction}>
            Please fill the form below to begin your mortgage application
            process
          </span>

          <div className={styles.grid}>
            <Controller
              name="hasAccessAccount"
              control={control}
              render={({ field }) => (
                <InputWithHeader
                  header={
                    isBusinessApplicant
                      ? "Do you use Access Bank for your Business Account?"
                      : "Do you have an Access Bank Account?"
                  }
                  placeholder="Select"
                  type="dropdown"
                  value={
                    field.value === true
                      ? "Yes"
                      : field.value === false
                      ? "No"
                      : ""
                  }
                  onPressDropDown={() =>
                    toggleDropdown("hasAccessAccount", true)
                  }
                  error={errors.hasAccessAccount?.message}
                />
              )}
            />

            {hasAccessAccount ? (
              <>
                <Controller
                  name="accountNumber"
                  control={control}
                  render={({ field }) => (
                    <InputWithHeader
                      header={
                        isBusinessApplicant
                          ? "Enter your Access Bank Business Account Number"
                          : "Access Bank Account Number"
                      }
                      placeholder="Enter Account Number"
                      type="string"
                      value={field.value || ""}
                      formKey="accountNumber"
                      handleChange={(_, val) => field.onChange(val)}
                      error={errors.accountNumber?.message}
                      textBelow={
                        accountVerified ? verifiedAccountName : undefined
                      }
                    />
                  )}
                />

                {accountResponse && !accountVerified && (
                  <div className={styles["account-otp-prompt"]}>
                    <span>
                      You won&apos;t be able to generate your statement
                      digitally until you validate your account.{" "}
                    </span>
                    <span
                      role="button"
                      className={styles["account-otp-prompt-link"]}
                      onClick={() => setShowAccountOtpModal(true)}
                    >
                      Click here to verify
                    </span>
                  </div>
                )}
              </>
            ) : null}

            {hasAccessAccount === false ? (
              <>
                <Controller
                  name="accountNumber"
                  control={control}
                  render={({ field }) => (
                    <InputWithHeader
                      header="Account Number"
                      placeholder="Enter Account Number"
                      type="string"
                      value={field.value || ""}
                      formKey="accountNumber"
                      handleChange={(_, val) => field.onChange(val)}
                      error={errors.accountNumber?.message}
                    />
                  )}
                />

                <Controller
                  name="accountName"
                  control={control}
                  render={({ field }) => (
                    <InputWithHeader
                      header="Account Name"
                      placeholder="Enter Account Name"
                      type="string"
                      value={field.value || ""}
                      formKey="accountName"
                      handleChange={(_, val) => field.onChange(val)}
                      error={errors.accountName?.message}
                    />
                  )}
                />

                <Controller
                  name="bankName"
                  control={control}
                  render={({ field }) => (
                    <InputWithHeader
                      header="Bank Name"
                      placeholder="Enter Bank Name"
                      type="dropdown"
                      value={field.value || ""}
                      onPressDropDown={() => toggleDropdown("bankName", true)}
                      error={errors.bankName?.message}
                    />
                  )}
                />
              </>
            ) : null}

            {/* Common to all */}
            <Controller
              name="propertyPhoto"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Property Photo"
                  placeholder="Upload property photo"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.propertyPhoto}
                />
              )}
            />
            <Controller
              name="meansOfIdentification"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Means of Identification (NIN, Voter’s Card or International Passport)"
                  placeholder="Upload Means of Identification"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.meansOfIdentification}
                />
              )}
            />
            <Controller
              name="statementOfAccount"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header={
                    hasAccessAccount === false
                      ? "Statement of Bank Account for the Last 12 Months (Other Bank)"
                      : "Statement of Bank Account for the Last 12 Months (Access Bank)"
                  }
                  placeholder="Upload Bank Account Statement"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.statementOfAccount}
                  onGenerateClick={
                    hasAccessAccount === true && accountVerified
                      ? () => setShowGenerateStatementModal(true)
                      : undefined
                  }
                  hasGenerated={statementGenerated}
                />
              )}
            />
            <Controller
              name="titledDocument"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Titled Document of Property"
                  placeholder="Upload titled document"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.titledDocument}
                />
              )}
            />
            <Controller
              name="aceptanceLetter"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Offer & Acceptance Letter of Property Owner"
                  placeholder="Upload Acceptance Letter of Property Owner"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.aceptanceLetter}
                />
              )}
            />

            {/* Salary only */}
            {!isBusinessApplicant && (
              <>
                <Controller
                  name="letterOfEmployment"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Letter of Employment"
                      placeholder="Upload letter of employment"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.letterOfEmployment}
                    />
                  )}
                />
                <Controller
                  name="paySlip"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Last 6 months Payslip"
                      placeholder="Upload 6 months payslip"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.paySlip}
                    />
                  )}
                />
              </>
            )}

            {/* Nigeria Salary only: Evidence of Payment */}
            {!isBusinessApplicant && !isDiaspora && (
              <Controller
                name="evidenceOfPayment"
                control={control}
                render={({ field }) => (
                  <FileUploadInput
                    header="Evidence of Additional Payment for the Past 12 Months"
                    placeholder="Upload evidence of payment"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.evidenceOfPayment}
                  />
                )}
              />
            )}

            {/* Diaspora required: Proof of Residence + Tax Evidence */}
            {isDiaspora && (
              <>
                <Controller
                  name="proofOfResidence"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Proof of Residence"
                      placeholder="Upload proof of residence"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.proofOfResidence}
                    />
                  )}
                />
                <Controller
                  name="taxEvidence"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Tax Evidence"
                      placeholder="Upload tax evidence"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.taxEvidence}
                    />
                  )}
                />
              </>
            )}

            {/* Business only */}
            {isBusinessApplicant && (
              <Controller
                name="businessRegistration"
                control={control}
                render={({ field }) => (
                  <FileUploadInput
                    header="Business Registration Document"
                    placeholder="Upload business registration document"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.businessRegistration}
                  />
                )}
              />
            )}

            {/* Diaspora optional: W2 + National Insurance */}
            {isDiaspora && (
              <>
                <Controller
                  name="w2"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="W2 (Optional)"
                      placeholder="Upload W2 document"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.w2}
                    />
                  )}
                />
                <Controller
                  name="nationalInsurance"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="National Insurance (Optional)"
                      placeholder="Upload national insurance document"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.nationalInsurance}
                    />
                  )}
                />
              </>
            )}
          </div>

          <div className={styles["bottom-cont"]}>
            <div
              className={styles["back-but"]}
              role="button"
              onClick={() => history.goBack()}
            >
              <span>Back</span>
            </div>
            <button type="submit" className={styles["submit-btn"]}>
              Submit Application
            </button>
          </div>
        </div>
      </form>

      <ModalLayout
        modalOpen={dropDownStates.hasAccessAccount}
        onClickOut={() => toggleDropdown("hasAccessAccount", false)}
      >
        <PopUp
          options={["Yes", "No"]}
          click={(name) => {
            setValue("hasAccessAccount", name === "Yes", {
              shouldValidate: true,
            });
            toggleDropdown("hasAccessAccount", false);
          }}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={dropDownStates.bankName}
        onClickOut={() => toggleDropdown("bankName", false)}
      >
        <PopUp
          options={banks.map((bank) => bank.bankName)}
          click={(name) => {
            setValue("bankName", name, { shouldValidate: true });
            toggleDropdown("bankName", false);
          }}
          title="SELECT BANK"
          searchable={true}
        />
      </ModalLayout>

      <ModalLayout
        modalOpen={submitSuccessful}
        onClickOut={() => clickOutSuccessModal()}
      >
        <SuccessModal
          onClose={() => clickOutSuccessModal()}
          message="Your Mortgage application has been submitted, you would be contacted shortly. Kindly check your email for a copy of your submitted application."
        />
      </ModalLayout>

      {showGenerateStatementModal && (
        <GenerateStatementModal
          onClose={() => setShowGenerateStatementModal(false)}
          onGenerate={handleGenerateStatement}
          isGenerating={isGeneratingStatement}
        />
      )}

      {showAccountOtpModal && (
        <ValidateAccountOtpModal
          onClose={() => setShowAccountOtpModal(false)}
          maskedContact={accountResponse?.maskedContact || ""}
          isVerifying={isVerifyingAccountOtp}
          onVerify={handleVerifyAccountOtp}
          onResend={handleResendAccountOtp}
        />
      )}
    </>
  );
};

export default DocumentUploadExtMort;
